/**
 * Planning a trip.
 *
 * Either from a saved route - most trips repeat, and typing the same four
 * places every week is how people stop bothering - or by picking the stops for
 * a one-off.
 *
 * The rules about what makes a valid trip live on the server, in the same
 * function the API uses, so this checks only enough to keep the button honest
 * and lets the server have the last word.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, Plus, Trash2 } from 'lucide-react';
import {
  ApiClientError,
  type CreateJourneyInput,
  type JourneyStopKind,
  type RouteRow,
} from '@mouldtrack/shared';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Field, Select } from '../../components/ui/field.js';
import { Code } from '../../components/ui/primitives.js';
import { listRoutes } from '../../api/client.js';
import { useMasterOptions, useMoulds } from '../../db/queries.js';

/** One row of the stop builder. A blank site is a row not yet filled in. */
interface Draft {
  locationId: string;
}

export function NewJourneyDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (input: CreateJourneyInput) => Promise<void>;
}) {
  const locations = useMasterOptions('locations');
  const moulds = useMoulds({});
  const [routes, setRoutes] = useState<RouteRow[] | null>(null);

  const [mouldId, setMouldId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [drafts, setDrafts] = useState<Draft[]>([{ locationId: '' }, { locationId: '' }]);
  const [startNow, setStartNow] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void listRoutes()
      .then((r) => {
        if (!cancelled) setRoutes(r);
      })
      // A saved route is a convenience. Not being able to list them should not
      // stop somebody typing the stops out.
      .catch(() => {
        if (!cancelled) setRoutes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const siteName = useMemo(
    () => new Map((locations ?? []).map((l) => [l.value, l.label])),
    [locations],
  );

  const usingRoute = routeId !== '';
  const stops = drafts.filter((d) => d.locationId !== '');
  const ready =
    mouldId !== '' && (usingRoute || (stops.length >= 2 && stops.length === drafts.length));

  /** START, then CHECKPOINTs, then DESTINATION - the order is the positions. */
  const kindAt = (i: number, total: number): JourneyStopKind =>
    i === 0 ? 'START' : i === total - 1 ? 'DESTINATION' : 'CHECKPOINT';

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await onCreate(
        usingRoute
          ? { mouldId, routeId, start: startNow }
          : {
              mouldId,
              stops: stops.map((d, i) => ({
                locationId: d.locationId,
                kind: kindAt(i, stops.length),
              })),
              start: startNow,
            },
      );
      onClose();
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'That journey could not be planned.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Plan a journey"
      description="The places a mould will pass through, in order. It fills itself in as the tool is scanned along the way."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={!ready}>
            {startNow ? 'Plan and start' : 'Save as planned'}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-3">
        <Field label="Which mould" required>
          <Select
            value={mouldId}
            onChange={(e) => setMouldId(e.target.value)}
            placeholder="Choose a mould…"
            options={(moulds ?? []).map((m) => ({
              value: m.id,
              label: `${m.assetCode} - ${m.name}`,
            }))}
          />
        </Field>

        {routes && routes.length > 0 ? (
          <Field label="Use a saved route" hint="Or leave this and pick the stops below.">
            <Select
              value={routeId}
              onChange={(e) => setRouteId(e.target.value)}
              placeholder="Pick the stops myself"
              options={routes.map((r) => ({ value: r.id, label: `${r.code} - ${r.name}` }))}
            />
          </Field>
        ) : null}

        {usingRoute ? (
          <div className="rounded-lg border border-border bg-surface-sunken p-3 text-sm">
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Stops</p>
            <ol className="flex flex-col gap-1">
              {(routes ?? [])
                .find((r) => r.id === routeId)
                ?.stops.map((s) => (
                  <li key={s.position} className="flex items-center gap-2">
                    <Code>{s.position + 1}</Code>
                    {s.location.name}
                    {s.kind !== 'CHECKPOINT' ? (
                      <span className="text-xs text-muted-foreground">
                        {s.kind === 'START' ? 'start' : 'destination'}
                      </span>
                    ) : null}
                  </li>
                ))}
            </ol>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Stops, in order</span>
            {drafts.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                  {kindAt(i, drafts.length) === 'START'
                    ? 'Start'
                    : kindAt(i, drafts.length) === 'DESTINATION'
                      ? 'Destination'
                      : `Stop ${i}`}
                </span>
                <Select
                  value={d.locationId}
                  onChange={(e) =>
                    setDrafts((prev) =>
                      prev.map((p, j) => (j === i ? { locationId: e.target.value } : p)),
                    )
                  }
                  className="flex-1"
                  placeholder="Choose a site…"
                  options={(locations ?? []).map((l) => ({ value: l.value, label: l.label }))}
                />
                {/* A trip needs a start and an end, so those two cannot go. */}
                {drafts.length > 2 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove stop ${i + 1}`}
                    onClick={() => setDrafts((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                ) : (
                  <span className="w-9 shrink-0" />
                )}
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              className="self-start"
              onClick={() =>
                // Added before the destination: a stop on the way is never the
                // last place the tool goes.
                setDrafts((prev) => [...prev.slice(0, -1), { locationId: '' }, prev[prev.length - 1]!])
              }
            >
              <Plus className="size-4" /> Add a stop on the way
            </Button>
            {stops.length >= 2 ? (
              <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                {stops.map((d, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {i > 0 ? <ArrowDown className="size-3 -rotate-90" aria-hidden /> : null}
                    {siteName.get(d.locationId) ?? '?'}
                  </span>
                ))}
              </p>
            ) : null}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={startNow}
            onChange={(e) => setStartNow(e.target.checked)}
            className="size-4"
          />
          Start it now
          <span className="text-xs text-muted-foreground">
            (otherwise it waits until somebody starts it, or the tool is scanned)
          </span>
        </label>
      </div>
    </Dialog>
  );
}
